import { Alert, Form, Input, Modal, Radio, Space, notification } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import {
  MILESTONE_STATUS_LABEL,
  RECRUIT_MILESTONE_CODES,
  RECRUIT_MILESTONE_LABEL,
} from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { RecruitMilestoneCode, RecruitView } from 'pages/star-shared/types';
import { formatDate, personName } from 'pages/star-shared/utils';
import StarAmount from 'pages/star-shared/components/star-amount';
import { colors } from 'theme/tokens';

/** Ngày hạn của mốc (YYYY-MM-DD, không gồm) — windows trong RecruitView. */
export function milestoneDeadline(r: RecruitView, code: RecruitMilestoneCode): string | null {
  if (!r.windows) return null;
  if (code === 'QUALIFY') return r.windows.qualifyEnd;
  if (code === 'RETAIN') return r.windows.retainAt;
  return r.windows.standoutEnd;
}

/**
 * Mốc có xác nhận tay được không (gợi ý phía trình duyệt; máy chủ quyết định cuối):
 * phiếu đang theo dõi; mốc PENDING, hoặc FAILED nhưng chưa quá `graceDays` sau hạn mốc;
 * "Giữ chân" cần "Đạt chuẩn" đã đạt/đã trả.
 */
export function confirmState(
  r: RecruitView,
  code: RecruitMilestoneCode,
  graceDays: number
): { ok: boolean; reason?: string } {
  if (r.status !== 'TRACKING') return { ok: false, reason: 'Phiếu không còn theo dõi.' };
  const m = r.milestones.find((x) => x.code === code);
  if (!m) return { ok: false, reason: 'Phiếu chưa có mốc này.' };
  if (code === 'RETAIN') {
    const qualify = r.milestones.find((x) => x.code === 'QUALIFY');
    if (!qualify || !['ACHIEVED', 'PAID'].includes(qualify.status)) {
      return { ok: false, reason: 'Cần "Đạt chuẩn" trước.' };
    }
  }
  if (m.status === 'PENDING') return { ok: true };
  if (m.status === 'FAILED') {
    const end = milestoneDeadline(r, code);
    if (!end) return { ok: true };
    const last = dayjs(end).add(graceDays, 'day');
    return dayjs().startOf('day').isBefore(last, 'day')
      ? { ok: true }
      : { ok: false, reason: `Quá hạn xác nhận tay (${formatDate(last.format('YYYY-MM-DD'))}).` };
  }
  if (m.status === 'ACHIEVED') {
    return { ok: false, reason: m.waitingCap ? 'Đã đạt, chờ trần tháng sau.' : 'Đã đạt, chờ trả.' };
  }
  return { ok: false, reason: `Mốc đã "${MILESTONE_STATUS_LABEL[m.status]}".` };
}

interface Props {
  recruit: RecruitView | null;
  graceDays: number;
  onClose: () => void;
  onDone: (recruit: RecruitView | null) => void;
}

// "Xác nhận tay" — PATCH /stars/admin/recruits/:id/confirm { milestone, note } (SPEC §3.2).
const ConfirmMilestoneModal = ({ recruit, graceDays, onClose, onDone }: Props) => {
  const [form] = Form.useForm<{ milestone?: RecruitMilestoneCode; note?: string }>();
  const [loading, setLoading] = useState(false);
  const open = !!recruit;

  useEffect(() => {
    if (!recruit) return;
    const first = RECRUIT_MILESTONE_CODES.find((c) => confirmState(recruit, c, graceDays).ok);
    form.setFieldsValue({ milestone: first, note: undefined });
  }, [recruit, graceDays, form]);

  const handleClose = () => {
    if (loading) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: { milestone?: RecruitMilestoneCode; note?: string }) => {
    if (!recruit || !values.milestone) return;
    setLoading(true);
    try {
      const res: any = await AxiosUtils.patch(`${API.starsRecruits}/${recruit.id}/confirm`, {
        milestone: values.milestone,
        note: values.note?.trim(),
      });
      notification.success({
        message: `Đã xác nhận mốc "${RECRUIT_MILESTONE_LABEL[values.milestone]}" cho ${personName(
          recruit.creator
        )}.`,
        description: 'Sao trả cho người tuyển ngay nếu còn trong trần tháng, không thì tháng sau.',
      });
      form.resetFields();
      onDone(res?.data && typeof res.data === 'object' ? (res.data as RecruitView) : null);
      onClose();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Xác nhận tay mốc tuyển dụng"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={560}
    >
      {recruit && (
        <div style={{ marginBottom: 12 }}>
          Creator <strong>{personName(recruit.creator)}</strong> · người tuyển{' '}
          <strong>{personName(recruit.recruiter)}</strong> · bắt đầu {formatDate(recruit.startDate)}
        </div>
      )}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Chỉ dùng khi thiếu dữ liệu cá nhân (vd. nhóm nhảy live chung một kênh) và bạn đã kiểm chứng. Bắt buộc ghi lý do; hạn xác nhận là ${graceDays} ngày sau hạn mốc. Sao đã trả không thu hồi.`}
      />
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Form.Item
          label="Mốc"
          name="milestone"
          rules={[{ required: true, message: 'Chọn mốc cần xác nhận.' }]}
        >
          <Radio.Group>
            <Space direction="vertical">
              {recruit &&
                RECRUIT_MILESTONE_CODES.map((code) => {
                  const state = confirmState(recruit, code, graceDays);
                  const m = recruit.milestones.find((x) => x.code === code);
                  return (
                    <Radio key={code} value={code} disabled={!state.ok}>
                      <strong>{RECRUIT_MILESTONE_LABEL[code]}</strong>{' '}
                      {m && <StarAmount value={m.star} strong={false} />}
                      {state.reason && (
                        <span style={{ color: colors.textSecondary, marginLeft: 8, fontSize: 12 }}>
                          {state.reason}
                        </span>
                      )}
                    </Radio>
                  );
                })}
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label="Lý do xác nhận"
          name="note"
          rules={[
            { required: true, whitespace: true, message: 'Vui lòng ghi lý do.' },
            {
              validator: (_, value?: string) =>
                !value || value.trim().length >= 5
                  ? Promise.resolve()
                  : Promise.reject(new Error('Lý do ít nhất 5 ký tự.')),
            },
            { max: 255, message: 'Tối đa 255 ký tự.' },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={255}
            showCount
            placeholder="VD: live chung kênh nhóm, đã đối chiếu lịch live 20/20 ngày"
          />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text="Xác nhận mốc"
            bgColor="secondary-green"
            htmlType="submit"
            loading={loading}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default ConfirmMilestoneModal;
